import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, X } from 'lucide-react';
import { useSection } from '../../hooks/useSection';
import { PREVENTE_DEADLINE } from '../../lib/prevente';
import { useSiteBanner } from '../../hooks/useCommerce';

const DISMISS_KEY = 'proxy_prevente_bar_dismissed';

const PreventeBanner: React.FC = () => {
  const { section } = useSection('home_prevente');
  const { banner } = useSiteBanner('home');
  const targetMs = banner?.deadline ? new Date(banner.deadline).getTime() : PREVENTE_DEADLINE.getTime();
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (stored) {
      const at = new Date(parseInt(stored, 10));
      if ((Date.now() - at.getTime()) / (1000 * 60 * 60) < 24) {
        setDismissed(true);
      }
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const target = targetMs;
    const tick = () => {
      const diff = target - new Date().getTime();
      if (diff < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0 });
      } else {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        });
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [dismissed, targetMs]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (section && section.is_visible === false) return null;
  if (dismissed) return null;
  const linkTo = banner?.cta_url || section?.button_url || '/products';
  const labelOverride = banner?.title || section?.title;

  return (
    <Link
      to={linkTo}
      translate="no"
      className="notranslate block border-b border-primary-700/30 bg-primary-600 text-white"
      style={{ transform: 'translate3d(0,0,0)', WebkitTransform: 'translate3d(0,0,0)' }}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between gap-2 px-1 py-2 text-xs sm:text-sm">
          <div className="flex flex-1 items-center justify-center gap-2 sm:gap-3">
            <Flame size={14} strokeWidth={2.5} className="animate-pulse text-primary-100 shrink-0" />
            <span className="font-bold tracking-wide truncate">
              {labelOverride ? (
                <>{labelOverride} · </>
              ) : (
                <>
                  <span className="hidden sm:inline">PRÉVENTE EXCLUSIVE · </span>
                  <span className="sm:hidden">PRÉVENTE · </span>
                </>
              )}
              Fin dans {countdown.days > 0 ? `${countdown.days}j ` : ''}{countdown.hours}h {countdown.minutes}m
            </span>
            <ArrowRight size={14} strokeWidth={2.5} className="shrink-0 hidden xs:inline" />
          </div>
          <button
            onClick={handleDismiss}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Fermer le bandeau prévente"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default PreventeBanner;
